import {
  markDragGestureClickSuppression,
  markPointerDownSelection,
} from '@components/Viewer3D/pointerSelectionGuard'
import { ThreeEvent, useFrame, useThree } from '@react-three/fiber'
import { MODULAR_FLOOR_DRAG_MIN_PX } from '@utils/modularSnap/config'
import { useModularUiStore } from '@utils/modularSnap/modularUiStore'
import { useAppStore } from '@store'
import { useEffect, useRef, useState } from 'react'
import type { Group, Matrix4 as Matrix4Type } from 'three'
import { Matrix4, Plane, Quaternion, Vector3 } from 'three'

type DragHandler = (l: Matrix4, dl: Matrix4, w: Matrix4, dw: Matrix4) => void

type Options = {
  enabled: boolean
  id: string
  matrix: Matrix4Type
  isBlocked?: boolean
  isModalOpen?: boolean
  mode: string
  currentObjectID: string
  /**
   * `floor`: solo planta (XZ), Y fija.
   * `free`: plano frente a cámara → X/Y/Z (cojines: subir/bajar sin gizmo).
   */
  dragMode?: 'floor' | 'free'
  /** Grupo raíz con `matrix` del item; se sincroniza tras `onDrag` con la posición del store. */
  matrixRootRef: React.RefObject<Group>
  onDragStart: () => void
  onDrag: DragHandler
  onDragEnd: () => void
  onPointerDownSelect: (e: ThreeEvent<PointerEvent>) => void
}

export function useMeshFloorDrag3D({
  enabled,
  id,
  matrix,
  isBlocked = false,
  isModalOpen = false,
  dragMode = 'floor',
  matrixRootRef,
  onDragStart,
  onDrag,
  onDragEnd,
  onPointerDownSelect,
}: Options) {
  const { camera, raycaster, pointer, controls } = useThree()
  const [isDragging, setIsDragging] = useState(false)
  const [dragPlane, setDragPlane] = useState<Plane | null>(null)
  const [initialDragPosition, setInitialDragPosition] = useState<Vector3 | null>(null)
  const [initialDragMatrix, setInitialDragMatrix] = useState<Matrix4 | null>(null)

  const isPointerDownRef = useRef(false)
  const isDraggingRef = useRef(false)
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const hasMovedRef = useRef(false)
  const dragActiveRef = useRef(false)
  const gestureItemRef = useRef<string | null>(null)
  const capturedPointerIdRef = useRef<number | null>(null)
  const dragModeRef = useRef(dragMode)
  const grabOffsetRef = useRef(new Vector3())
  dragModeRef.current = dragMode

  const canInteract = enabled && !isBlocked && !isModalOpen

  const setCameraControlsEnabled = (cameraEnabled: boolean) => {
    const ctrl = controls as { enabled?: boolean; stop?: () => void } | null
    if (!ctrl) return
    if (!cameraEnabled && typeof ctrl.stop === 'function') {
      ctrl.stop()
    }
    if (typeof ctrl.enabled === 'boolean') {
      ctrl.enabled = cameraEnabled
    }
  }

  const armPointerGesture = (nativeEvent?: PointerEvent) => {
    useModularUiStore.getState().setItemDragActive(true)
    setCameraControlsEnabled(false)
    if (nativeEvent && typeof nativeEvent.target !== 'undefined') {
      const target = nativeEvent.target as Element | null
      if (target?.setPointerCapture) {
        try {
          target.setPointerCapture(nativeEvent.pointerId)
          capturedPointerIdRef.current = nativeEvent.pointerId
        } catch {
          capturedPointerIdRef.current = null
        }
      }
    }
  }

  const releasePointerGesture = (nativeEvent?: PointerEvent) => {
    const pointerId = capturedPointerIdRef.current
    if (pointerId !== null && nativeEvent?.target) {
      const target = nativeEvent.target as Element | null
      if (target?.releasePointerCapture) {
        try {
          target.releasePointerCapture(pointerId)
        } catch {
          // El puntero ya se liberó.
        }
      }
    }
    capturedPointerIdRef.current = null
    useModularUiStore.getState().setItemDragActive(false)
    if (!useAppStore.getState().isModalOpen) {
      setCameraControlsEnabled(true)
    }
  }

  useEffect(() => {
    if (!canInteract) return
    if (isDragging) {
      document.body.style.cursor = 'grabbing'
      return () => {
        document.body.style.cursor = ''
      }
    }
    return undefined
  }, [canInteract, isDragging])

  const resetDragState = () => {
    isPointerDownRef.current = false
    isDraggingRef.current = false
    dragActiveRef.current = false
    gestureItemRef.current = null
    pointerStartRef.current = null
    setIsDragging(false)
    setDragPlane(null)
    setInitialDragPosition(null)
    setInitialDragMatrix(null)
  }

  const finishDrag = (nativeEvent?: PointerEvent) => {
    if (!isPointerDownRef.current && !isDraggingRef.current && !dragActiveRef.current) return

    const wasDragging = dragActiveRef.current || isDraggingRef.current
    resetDragState()
    if (wasDragging) {
      markDragGestureClickSuppression()
      onDragEnd()
    }
    releasePointerGesture(nativeEvent)
  }

  const beginDrag = () => {
    if (isDraggingRef.current || gestureItemRef.current !== String(id)) return

    const currentPosition = new Vector3()
    matrix.decompose(currentPosition, new Quaternion(), new Vector3())

    const plane = new Plane()
    if (dragModeRef.current === 'free') {
      const camDir = new Vector3()
      camera.getWorldDirection(camDir)
      plane.setFromNormalAndCoplanarPoint(camDir, currentPosition)
    } else {
      plane.set(new Vector3(0, 1, 0), -currentPosition.y)
    }

    raycaster.setFromCamera(pointer, camera)
    const initialIntersection = new Vector3()
    const hasInitialIntersection = raycaster.ray.intersectPlane(plane, initialIntersection)
    const hit = hasInitialIntersection ? initialIntersection : currentPosition.clone()
    grabOffsetRef.current.copy(hit).sub(currentPosition)

    isDraggingRef.current = true
    dragActiveRef.current = true
    hasMovedRef.current = true
    setIsDragging(true)
    setDragPlane(plane)
    setInitialDragPosition(hit.clone())
    setInitialDragMatrix(matrix.clone())
    armPointerGesture()
    onDragStart()
  }

  const tryBeginDragFromPointer = (clientX: number, clientY: number) => {
    if (!isPointerDownRef.current || !pointerStartRef.current || isDraggingRef.current) return
    if (gestureItemRef.current !== String(id)) return

    const px = Math.hypot(clientX - pointerStartRef.current.x, clientY - pointerStartRef.current.y)
    if (px >= MODULAR_FLOOR_DRAG_MIN_PX) {
      beginDrag()
    }
  }

  useEffect(() => {
    if (!canInteract) return

    const handlePointerUp = (e: PointerEvent) => {
      if (e.button !== 0) return
      if (gestureItemRef.current !== String(id)) return
      finishDrag(e)
    }

    // Umbral de arrastre a nivel window: sigue funcionando aunque el puntero salga de la malla.
    // NO usar stopPropagation: bloquearía los eventos de R3F.
    const handleGlobalPointerMove = (e: PointerEvent) => {
      if (gestureItemRef.current !== String(id)) return
      if (!isPointerDownRef.current) return
      if ((e.buttons & 1) === 0) {
        finishDrag(e)
        return
      }
      tryBeginDragFromPointer(e.clientX, e.clientY)
    }

    window.addEventListener('pointerup', handlePointerUp, true)
    window.addEventListener('pointercancel', handlePointerUp, true)
    window.addEventListener('pointermove', handleGlobalPointerMove, true)
    window.addEventListener('blur', finishDrag)

    return () => {
      window.removeEventListener('pointerup', handlePointerUp, true)
      window.removeEventListener('pointercancel', handlePointerUp, true)
      window.removeEventListener('pointermove', handleGlobalPointerMove, true)
      window.removeEventListener('blur', finishDrag)
    }
  }, [canInteract, id, onDragEnd])

  useFrame(() => {
    if (canInteract && isDraggingRef.current && gestureItemRef.current === String(id)) {
      setCameraControlsEnabled(false)
    }

    if (
      !canInteract ||
      !isDraggingRef.current ||
      !dragPlane ||
      !initialDragPosition ||
      !initialDragMatrix
    ) {
      return
    }
    if (gestureItemRef.current !== String(id)) return
    if (String(useAppStore.getState().currentObjectID) !== String(id)) return

    const free = dragModeRef.current === 'free'
    if (free) {
      const camDir = new Vector3()
      camera.getWorldDirection(camDir)
      const anchor = new Vector3()
      initialDragMatrix.decompose(anchor, new Quaternion(), new Vector3())
      // Mantener el plano frente a cámara pasando por la posición inicial del item.
      dragPlane.setFromNormalAndCoplanarPoint(camDir, anchor)
    }

    raycaster.setFromCamera(pointer, camera)
    const intersection = new Vector3()
    if (!raycaster.ray.intersectPlane(dragPlane, intersection)) return

    const initialPos = new Vector3()
    const initialRot = new Quaternion()
    const initialScale = new Vector3()
    initialDragMatrix.decompose(initialPos, initialRot, initialScale)

    let newPosition: Vector3
    if (free) {
      newPosition = intersection.clone().sub(grabOffsetRef.current)
      // Permitir bajar hasta el suelo; el mesh ya está asentado al origen.
      newPosition.y = Math.max(0, newPosition.y)
    } else {
      const deltaX = intersection.x - initialDragPosition.x
      const deltaZ = intersection.z - initialDragPosition.z
      newPosition = new Vector3(initialPos.x + deltaX, initialPos.y, initialPos.z + deltaZ)
    }

    const newMatrix = new Matrix4().compose(newPosition, initialRot, initialScale)
    const deltaMatrix = newMatrix.clone().multiply(initialDragMatrix.clone().invert())

    onDrag(newMatrix, deltaMatrix, newMatrix, deltaMatrix)

    const matrixRoot = matrixRootRef.current
    const latestMatrix = useAppStore.getState().items.get(String(id))?.matrix as Matrix4 | undefined
    if (matrixRoot && latestMatrix) {
      matrixRoot.matrix.copy(latestMatrix)
      matrixRoot.matrixAutoUpdate = false
      matrixRoot.updateMatrixWorld(true)
    }
  })

  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    if (!canInteract || e.button !== 0) return
    e.stopPropagation()

    resetDragState()
    hasMovedRef.current = false
    isPointerDownRef.current = true
    gestureItemRef.current = String(id)
    pointerStartRef.current = { x: e.clientX, y: e.clientY }

    markPointerDownSelection(id)
    armPointerGesture(e.nativeEvent)
    onPointerDownSelect(e)
  }

  const handlePointerMove = (e: ThreeEvent<PointerEvent>) => {
    if (!canInteract || gestureItemRef.current !== String(id)) return
    if (!isPointerDownRef.current) return
    if ((e.buttons & 1) === 0) {
      finishDrag(e.nativeEvent)
      return
    }

    e.stopPropagation()
    tryBeginDragFromPointer(e.clientX, e.clientY)
  }

  const handlePointerUp = (e: ThreeEvent<PointerEvent>) => {
    if (gestureItemRef.current !== String(id)) return
    if (!isPointerDownRef.current && !isDraggingRef.current) return
    if (hasMovedRef.current) e.stopPropagation()
    finishDrag(e.nativeEvent)
  }

  return {
    meshPointerHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
    },
    suppressClick: () => hasMovedRef.current,
    isDragging,
  }
}
